import axios, { AxiosError } from 'axios';

import {
  InvoiceObjectData,
  InvoicePreviewDto,
} from 'root/apps/QuoteMachineIntegration/state/v1/InvoicePreviewDto';
import { formatRefId } from 'root/apps/QuoteMachineIntegration/utils/referenceHelpers';
import { datadogRum } from 'root/datadog/datadogRum';

const BASE_URL = '/admin/invoicing';
export default class InvoiceService {
  /**
   * Fetches invoice preview payload from backend and submits it to
   * create or update an InvoiceIntent in the invoicing-api v1 flow.
   *
   * @param saleID The sale ID for which the invoice intent should be created or updated.
   * @param creditAccountID The credit account ID from R-Series.
   * @param invoiceDataFromPreview The invoice data object to be sent in the PUT request.
   * @returns The Axios response of the PUT call to the invoicing API.
   */
  static async upsertInvoiceIntent(
    saleID: string,
    creditAccountID: string,
    invoiceDataFromPreview: InvoiceObjectData | undefined
  ) {
    if (!saleID || !creditAccountID) {
      throw new Error(`Missing path params: creditAccountID=${creditAccountID}, saleID=${saleID}`);
    }

    const transactionRef = formatRefId(saleID);

    let invoiceData = invoiceDataFromPreview;

    if (!invoiceData || Object.keys(invoiceData).length === 0) {
      // Log unexpected condition for monitoring
      datadogRum.addAction('invoice_data_missing_fallback', {
        transactionRef,
        message: 'invoiceData was missing during upsert, fetching preview data as fallback',
      });

      const response = await axios.post<{ invoiceData: InvoicePreviewDto }>(
        `${BASE_URL}/invoice-preview-data/${saleID}`,
        {}
      );

      invoiceData = response.data?.invoiceData?.objectData;

      if (!invoiceData || Object.keys(invoiceData).length === 0) {
        throw new Error('Missing invoice data from response');
      }
    }

    const creditAccountRef = formatRefId(creditAccountID);

    try {
      // PUT to the invoice-intent endpoint
      const putUrl = `${BASE_URL}/v1/credit-accounts/${creditAccountRef}/transactions/${transactionRef}/invoice-intent`;
      return await axios.put(putUrl, invoiceData);
    } catch (error) {
      datadogRum.addError(error, {
        transactionRef,
        creditAccountRef,
        message: `Failed to create or update invoice intent for transactionRef: ${transactionRef}`,
        timeStamp: new Date().toISOString(),
      });
      throw error;
    }
  }
  /**
   * Retrieves the invoice for a given transaction reference.
   *
   * @param creditAccountID The credit account reference, it's represented by the credit account ID.
   * @param saleID The transaction reference whose invoice should be fetched, it's represented by the sale ID.
   * @returns The first invoice object from the array returned by the invoicing API.
   */
  static async getInvoiceByTransactionRef(creditAccountID: string, saleID: string) {
    if (!saleID || !creditAccountID) {
      throw new Error('Missing creditAccountID or saleID parameter');
    }

    const transactionRef = formatRefId(saleID);
    const creditAccountRef = formatRefId(creditAccountID);

    try {
      const url = `${BASE_URL}/v1/credit-accounts/${creditAccountRef}/invoices?transactionRef=${transactionRef}`;
      const response = await axios.get(url);

      // Extract the first invoice from the data array
      const invoices = response.data?.data;
      if (!invoices || !Array.isArray(invoices) || invoices.length === 0) {
        throw new Error('No invoice found for the given transaction reference');
      }

      return invoices[0];
    } catch (error) {
      datadogRum.addError(error, {
        transactionRef,
        creditAccountRef,
        message: `Failed to fetch invoice for transactionRef: ${transactionRef}`,
        timeStamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  /**
   * Deletes an invoice intent for a transaction.
   * This is idempotent - returns successfully even if the intent doesn't exist (404).
   *
   * @param creditAccountId The credit account ID from R-Series
   * @param saleId The sale/transaction ID
   */
  static async deleteInvoiceIntent(
    creditAccountId: string | number,
    saleId: string
  ): Promise<void> {
    if (!creditAccountId || creditAccountId === 0 || creditAccountId === '0') {
      return;
    }

    if (!saleId) {
      return;
    }

    const creditAccountRef = formatRefId(`${creditAccountId}`);
    const transactionRef = formatRefId(`${saleId}`);

    const url = `${BASE_URL}/v1/credit-accounts/${creditAccountRef}/transactions/${transactionRef}/invoice-intent`;

    try {
      await axios.delete(url);
    } catch (error) {
      const axiosError = error as AxiosError;

      if (axiosError.response?.status === 404) {
        return;
      }

      datadogRum.addError(error, {
        creditAccountRef,
        transactionRef,
        message: `Failed to delete invoice intent for transactionRef: ${transactionRef}`,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
}
